// Center column — the input + [GUESS] + [CHEAT] + guess history.
// Per-rule pass/fail explainer renders for the most recent failed
// guess so the player can see WHICH constraints they violated.

import { useState } from "react";
import { useLoopState } from "../lib/use-loop-state";
import { cheat, submitGuess } from "../lib/andon-loop";
import { formatPredicate } from "../lib/dsl";
import { explainCandidate } from "../lib/generator";

export function GuessPanel() {
  const state = useLoopState();
  const [draft, setDraft] = useState("");
  const [revealed, setRevealed] = useState(false);
  const [busy, setBusy] = useState(false);

  const handleGuess = async () => {
    if (!draft || busy) return;
    setBusy(true);
    try {
      await submitGuess(draft);
      setDraft("");
    } finally {
      setBusy(false);
    }
  };

  const handleCheat = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await cheat();
      setDraft("");
    } finally {
      setBusy(false);
    }
  };

  // The latest guess (whether won or lost) gets the per-rule breakdown.
  // Successful guesses skip the breakdown (it would just be all green).
  const latest = state.guesses[state.guesses.length - 1];
  const showBreakdown = latest && !latest.passed;
  const breakdown = showBreakdown ? explainCandidate(state.rules, latest.guess) : [];

  return (
    <section className="gp">
      <header className="gp-head">
        <span className="gp-title">YOUR GUESS</span>
        <button
          type="button"
          className="gp-reveal"
          onClick={() => setRevealed((r) => !r)}
          title="Show the current password (the [CHEAT] button uses this)"
        >
          {revealed ? "hide" : "reveal"} pw
        </button>
      </header>

      <div className="gp-pw-row">
        <span className="gp-pw-label">current password:</span>
        <span className="gp-pw-value">
          {revealed ? state.currentPassword : "•".repeat(state.currentPassword.length)}
        </span>
        <span className="gp-pw-len">{state.currentPassword.length} chars</span>
      </div>

      <form
        className="gp-form"
        onSubmit={(e) => { e.preventDefault(); void handleGuess(); }}
      >
        <input
          className="gp-input"
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="type your guess…"
          disabled={busy}
          autoComplete="off"
          autoCapitalize="off"
          spellCheck={false}
        />
        <div className="gp-buttons">
          <button type="submit" className="gp-btn gp-btn-guess" disabled={!draft || busy}>
            GUESS
          </button>
          <button
            type="button"
            className="gp-btn gp-btn-cheat"
            onClick={() => void handleCheat()}
            disabled={busy}
            title="Submit the current password verbatim — fast-forwards to the next cord pull"
          >
            CHEAT
          </button>
        </div>
      </form>

      {showBreakdown && (
        <div className="gp-breakdown">
          <header className="gp-bd-head">
            <span className="gp-bd-title">last guess: <code>{latest.guess}</code></span>
            <span className="gp-bd-verdict">rejected</span>
          </header>
          <ul className="gp-bd-list">
            {breakdown.map(({ rule, passed }) => (
              <li key={rule.id} className={passed ? "gp-bd-ok" : "gp-bd-fail"}>
                <span className="gp-bd-mark">{passed ? "✓" : "✗"}</span>
                <span className="gp-bd-name">{rule.name}</span>
                <span className="gp-bd-pred">{formatPredicate(rule.predicate)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="gp-history-head">
        <span>HISTORY</span>
        <span className="gp-history-count">{state.guesses.length}</span>
      </div>
      <div className="gp-history">
        {state.guesses.length === 0 ? (
          <div className="gp-history-empty">no guesses yet — try one.</div>
        ) : (
          <ul className="gp-history-list">
            {[...state.guesses].reverse().map((g, i) => (
              <li key={state.guesses.length - 1 - i} className={g.passed ? "gp-h-pass" : "gp-h-fail"}>
                <span className="gp-h-mark">{g.passed ? "✓" : "✗"}</span>
                <code className="gp-h-text">{g.guess}</code>
                <span className="gp-h-time">{new Date(g.ts).toLocaleTimeString()}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <Styles />
    </section>
  );
}

function Styles() {
  return (
    <style>{`
      .gp {
        display: flex; flex-direction: column;
        height: 100%;
        background: var(--bg-1);
        border: 2px solid var(--border);
        border-radius: var(--radius-2);
        overflow: hidden;
      }
      .gp-head {
        display: flex; align-items: center; gap: var(--space-2);
        padding: var(--space-3) var(--space-4);
        border-bottom: 2px solid var(--border);
        background: var(--bg-2);
      }
      .gp-title {
        flex: 1;
        font-family: var(--font-display);
        font-weight: 800;
        font-size: 12px;
        letter-spacing: 0.1em;
      }
      .gp-reveal {
        font-size: 11px;
        padding: 4px 10px;
      }
      .gp-pw-row {
        display: flex; align-items: baseline; gap: var(--space-3);
        padding: var(--space-3) var(--space-4);
        border-bottom: 2px dashed var(--border-soft);
      }
      .gp-pw-label {
        font-family: var(--font-mono);
        font-size: 11px;
        color: var(--fg-2);
      }
      .gp-pw-value {
        flex: 1;
        font-family: var(--font-mono);
        font-size: 18px;
        font-weight: 700;
        color: var(--blueprint);
        letter-spacing: 0.08em;
        word-break: break-all;
      }
      .gp-pw-len {
        font-family: var(--font-mono);
        font-size: 11px;
        font-weight: 700;
        color: var(--fg-2);
        background: var(--bg-2);
        border: 1px solid var(--border-soft);
        padding: 1px 7px;
        border-radius: 999px;
        white-space: nowrap;
      }
      .gp-form {
        padding: var(--space-4);
        display: flex; flex-direction: column;
        gap: var(--space-3);
      }
      .gp-input {
        font-size: 18px;
        padding: 10px 14px;
        font-weight: 600;
        letter-spacing: 0.06em;
      }
      .gp-buttons {
        display: flex; gap: var(--space-3);
      }
      .gp-btn {
        flex: 1;
        font-size: 14px;
        padding: 10px 0;
        font-weight: 800;
        letter-spacing: 0.05em;
      }
      .gp-btn-guess {
        background: var(--safety-green);
        color: #fff;
      }
      .gp-btn-guess:hover:not(:disabled) {
        background: #248c44;
      }
      .gp-btn-cheat {
        background: var(--caution-yellow);
        color: var(--fg-0);
      }
      .gp-btn-cheat:hover:not(:disabled) {
        background: #f5c12d;
      }

      .gp-breakdown {
        margin: 0 var(--space-4) var(--space-3) var(--space-4);
        background: var(--andon-red-soft);
        border: 2px solid var(--andon-red);
        border-radius: var(--radius-1);
        padding: var(--space-3);
      }
      .gp-bd-head {
        display: flex; align-items: baseline; gap: var(--space-2);
        margin-bottom: var(--space-2);
      }
      .gp-bd-title {
        flex: 1;
        font-family: var(--font-mono);
        font-size: 12px;
      }
      .gp-bd-title code {
        background: #fff;
        padding: 2px 6px;
        border-radius: 3px;
        font-weight: 700;
        color: var(--fg-0);
      }
      .gp-bd-verdict {
        font-family: var(--font-display);
        font-weight: 800;
        font-size: 11px;
        text-transform: uppercase;
        color: var(--andon-red);
      }
      .gp-bd-list {
        list-style: none;
        margin: 0;
        padding: 0;
        display: flex; flex-direction: column;
        gap: 4px;
      }
      .gp-bd-list li {
        display: flex; align-items: baseline; gap: 6px;
        font-family: var(--font-mono);
        font-size: 11px;
      }
      .gp-bd-mark {
        font-weight: 800;
        width: 14px;
      }
      .gp-bd-ok .gp-bd-mark   { color: var(--safety-green); }
      .gp-bd-fail .gp-bd-mark { color: var(--andon-red); }
      .gp-bd-name {
        color: var(--fg-0);
        font-weight: 700;
        min-width: 100px;
      }
      .gp-bd-pred {
        color: var(--fg-2);
        flex: 1;
      }

      .gp-history-head {
        display: flex; align-items: baseline; justify-content: space-between;
        padding: var(--space-2) var(--space-4);
        font-family: var(--font-display);
        font-weight: 800;
        font-size: 11px;
        letter-spacing: 0.1em;
        color: var(--fg-1);
        border-top: 2px solid var(--border-soft);
      }
      .gp-history-count {
        font-family: var(--font-mono);
        background: var(--bg-2);
        border: 1px solid var(--border-soft);
        padding: 1px 6px;
        border-radius: 999px;
        font-size: 10px;
        font-weight: 700;
      }
      .gp-history {
        flex: 1; overflow-y: auto;
        padding: 0 var(--space-4) var(--space-3) var(--space-4);
      }
      .gp-history-empty {
        font-style: italic;
        color: var(--fg-2);
        font-size: 12px;
        padding: var(--space-3) 0;
      }
      .gp-history-list {
        list-style: none;
        margin: 0;
        padding: 0;
        display: flex; flex-direction: column;
        gap: 4px;
      }
      .gp-history-list li {
        display: flex; align-items: center; gap: 8px;
        font-family: var(--font-mono);
        font-size: 12px;
        padding: 4px 8px;
        border-radius: var(--radius-1);
      }
      .gp-h-pass {
        background: var(--safety-green-soft);
      }
      .gp-h-fail {
        background: var(--bg-2);
      }
      .gp-h-mark {
        font-weight: 800;
        width: 14px;
      }
      .gp-h-pass .gp-h-mark { color: var(--safety-green); }
      .gp-h-fail .gp-h-mark { color: var(--andon-red); }
      .gp-h-text {
        flex: 1;
        color: var(--fg-0);
      }
      .gp-h-time {
        color: var(--fg-2);
        font-size: 10px;
      }
    `}</style>
  );
}
